<template>
  <v-navigation-drawer
    v-if="display.mdAndDown"
    v-model="showNavigation"
    app
    disable-route-watcher
    disable-resize-watcher
    temporary
    :mobile="false"
    class="app-drawer"
  >
    <v-list>
      <v-list-item class="mb-3 text-center">
        <router-link
          :to="homeRoute"
          @click="handleClick(null)"
        >
          <v-img
            src="@/images/logo.png"
            class="app-bar__logo"
            contain
            width="220"
          />
        </router-link>
      </v-list-item>
      <template
        v-for="(link, index) in navigationLinks"
        :key="'nav-drawer-'+index"
      >
        <v-list-item
          v-if="!link.hr"
          exact-path
          :to="!link.action ? $routeTo(link.to) : null"
          :target="link.target"
          :href="link.to ? link.to : null"
          active-class="primary--text"
          @click="handleClick(link.action)"
        >
          <template #prepend>
            <v-icon
              color="primary"
              :icon="link.icon"
              :title="link.text.toUpperCase()"
            />
          </template>
          <v-list-item-title
            class="app-drawer__title"
            v-text="link.text.toUpperCase()"
          />
        </v-list-item>
        <v-divider
          v-else-if="link.hr"
          class="my-3"
        />
      </template>
    </v-list>
    <template #append>
      <div class="pt-3">
        <v-list>
          <v-list-item
            v-for="(link, i) in bottomLinks"
            :key="i"
            :to="!link.action ? $routeTo(link.to) : null"
            :target="link.target"
            :href="link.to ? link.to : null"
            active-class="primary--text"
            exact-path
            @click="handleClick(link.action)"
          >
            <template #prepend>
              <v-icon
                color="primary"
                :icon="link.icon"
                :title="link.text"
              />
            </template>
            <v-list-item-title
              class="app-drawer__title"
              v-text="link.text"
            />
          </v-list-item>
        </v-list>
      </div>
    </template>
  </v-navigation-drawer>
</template>

<script lang="ts">
import {defineComponent, type PropType, ref} from 'vue'
import {mapWritableState} from "pinia"
import {useAppStore} from "@/stores/app"
import {useDisplay} from "vuetify"
import home from "@/mixins/home"
import profile from "@/mixins/profile"

interface Link {
  text: string
  to: string
  target?: string
  icon: string
  action?: () => void
  hr?: boolean
}

export default defineComponent({
  emits: ['logout', 'stop-impersonating', 'profile'],
  mixins: [home, profile],
  props: {
    navigationLinks: {
      type: Array as PropType<Link[]>,
      required: true,
    },
  },
  setup() {
    const display = ref(useDisplay())
    return {display: display}
  },
  computed: {
    ...mapWritableState(useAppStore, ['showNavigation']),
    bottomLinks() {
      let links: Link[] = []

      if(this.$auth.isGuest()) {
        links = [
          {text: 'LOGIN', to: this.ROUTES.LOGIN, icon: 'mdi-login'},
          {text: 'SIGN UP', to: this.ROUTES.REGISTER, icon: 'mdi-account-plus'},
        ]
      } else {
        if(this.$auth.user?.is_impersonated) {
          links = [
            {text: 'STOP IMPERSONATING', action: this.stopImpersonating, icon: 'mdi-account-switch', to: ''},
          ]
        }
        links.push({text: 'PROFILE', action: () => this.profile(), icon: 'mdi-account-outline', to: ''})
        links.push({text: 'LOGOUT', action: this.logout, icon: 'mdi-exit-to-app', to: ''})
      }

      return links
    }
  },
  methods: {
    async logout() {
      this.$emit('logout')
    },
    async stopImpersonating() {
      this.$emit('stop-impersonating')
    },
    async handleClick(action?: () => void) {
      this.showNavigation = false
      if (!action) {
        return
      }

      await this.$nextTick()

      try {
        await action()
      } catch (_error) {
        // do nothing
      }
    }
  },
})
</script>

<style scoped lang="scss">
.app-drawer {
  &__title {
    font-size: 15px;
  }
  ::-webkit-scrollbar {
    width: 8px;
  }
  ::-webkit-scrollbar-track {
    background: #fafafa;
  }
  ::-webkit-scrollbar-thumb {
    background-color: #EC5C29;
    border-radius: 14px;
    border: 3px solid #cecece;
  }
}
</style>
